import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';

/**
 * The filter selections currently expressed in the URL, as the analytics layer
 * wants them. Shared by the /news and /videos filter bars.
 */
export type FilterSelections = {
  divisions: string[];
  clubs: string[];
  search: string;
  tags: string[];
};

export type RawFilterParams = {
  division: string | null;
  club: string | null;
  search: string | null;
  tags: string | null;
};

const splitParam = (value: string | null): string[] => value?.split(',').filter(Boolean) ?? [];

// "all" is a sentinel the API expands; report it as ["all"] rather than
// splitting it into a tag literally named "all".
const selectionFor = (value: string | null): string[] => (value === 'all' ? ['all'] : splitParam(value));

export const readFilterSelections = ({ division, club, search, tags }: RawFilterParams): FilterSelections => ({
  divisions: selectionFor(division),
  clubs: selectionFor(club),
  search: search ?? '',
  tags: splitParam(tags),
});

export const hasAnyFilterSelection = ({ divisions, clubs, search, tags }: FilterSelections): boolean =>
  divisions.length > 0 || clubs.length > 0 || search !== '' || tags.length > 0;

/** Joins tag sources into the comma-separated string the API expects. */
export const joinTags = (parts: (string | null | undefined)[]): string | undefined =>
  parts.filter(Boolean).join(',') || undefined;

export type UrlFilterValues = {
  /** Raw params, always read, for effect deps and `filterByTags` fallbacks. */
  raw: RawFilterParams;
  /** The same values, but only when the list is configured to honour them. */
  tags?: string;
  club?: string;
  division?: string;
  search?: string;
};

/**
 * Reads the filter params a list component sends to the API. Values are only
 * surfaced when `enabled` (the list's `readFiltersFromUrl` flag) is set, so a
 * list that isn't paired with a filter bar ignores the URL entirely.
 */
export const useUrlFilterValues = (enabled = false): UrlFilterValues => {
  const [searchParams] = useSearchParams();

  // Memoized so `raw` is safe to use as an effect dep or pass to a memoized
  // child, as the type's doc comment promises.
  const raw: RawFilterParams = useMemo(
    () => ({
      division: searchParams.get('division'),
      club: searchParams.get('club'),
      search: searchParams.get('search'),
      tags: searchParams.get('tags'),
    }),
    [searchParams]
  );

  const pick = (value: string | null) => (enabled && value ? value : undefined);

  return {
    raw,
    tags: pick(raw.tags),
    club: pick(raw.club),
    division: pick(raw.division),
    search: pick(raw.search),
  };
};

/**
 * Reports a filter event once the URL filters change — skipping the initial
 * mount, so simply loading a filtered page is not counted as filtering, and
 * skipping URLs with nothing selected.
 */
export const useFilterTracking = (
  enabled = false,
  raw: RawFilterParams,
  track: (selections: FilterSelections) => void
): void => {
  const isInitialFilterMount = useRef(true);
  const { division, club, search, tags } = raw;

  useEffect(() => {
    if (!enabled) return;

    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false;
      return;
    }

    const selections = readFilterSelections({ division, club, search, tags });

    if (hasAnyFilterSelection(selections)) {
      track(selections);
    }
    // `track` is intentionally omitted: callers pass a module-level function and
    // re-running on identity changes would double-report.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, division, club, search, tags]);
};
